import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import {
  authService,
  PASSWORD_RULE,
  PASSWORD_RULE_MESSAGE,
  PASSWORD_BLOCKLISTED_MESSAGE,
  isPasswordBlocklisted,
} from '../api/auth';
import { colors } from '../../../theme';

/**
 * Step 2 of password recovery (TCH-02-FR-06): exchange the emailed code for a
 * new password. Reached from ForgotPassword (address in router state) or
 * directly from an email link carrying ?email=…&code=….
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const stateEmail = (location.state as { email?: string } | null)?.email;

  const [email, setEmail] = useState(stateEmail || searchParams.get('email') || '');
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordError('');

    if (!email.trim()) {
      setError('Ingresa el correo de tu cuenta.');
      return;
    }
    if (!/^\d{6}$/.test(code.trim())) {
      setError('El código son 6 dígitos. Revísalo e inténtalo de nuevo.');
      return;
    }
    if (!PASSWORD_RULE.test(password)) {
      setError(PASSWORD_RULE_MESSAGE);
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword({
        email: email.trim(),
        code: code.trim(),
        newPassword: password,
      });
      navigate('/login', {
        replace: true,
        state: { passwordReset: true, email: email.trim() },
      });
    } catch (err: any) {
      const message: string = err?.message || '';
      if (isPasswordBlocklisted(err)) {
        // TCH-02-FR-03: the code was not spent; choosing another is enough.
        setPasswordError(PASSWORD_BLOCKLISTED_MESSAGE);
      } else if (/expired/i.test(message)) {
        setError('Ese código ya venció. Solicita uno nuevo desde "Olvidé mi contraseña".');
      } else if (/mismatch|invalid|not match/i.test(message)) {
        setError('El código no es correcto. Verifícalo o solicita uno nuevo.');
      } else {
        setError(message || 'No pudimos cambiar tu contraseña. Inténtalo de nuevo.');
      }
    } finally {
      setLoading(false);
    }
  };

  const passwordAdornment = (
    <InputAdornment position="end">
      <IconButton
        size="small"
        onClick={() => setShowPassword((v) => !v)}
        edge="end"
        aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      >
        {showPassword ? (
          <VisibilityOffRoundedIcon fontSize="small" />
        ) : (
          <VisibilityRoundedIcon fontSize="small" />
        )}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 140px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f7fb',
        py: 6,
        px: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 4,
          border: `1px solid ${colors.grey[200]}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: colors.primary.main }}>
              IQMaxx
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <LockResetRoundedIcon sx={{ fontSize: 44, color: colors.primary.main }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center', mb: 1 }}>
            Crea una contraseña nueva
          </Typography>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: colors.text.secondary, mb: 1 }}
          >
            {email ? (
              <>
                Enviamos un código de 6 dígitos a <strong>{email}</strong>.
              </>
            ) : (
              <>Ingresa el código que te enviamos por correo.</>
            )}
          </Typography>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: colors.text.secondary, mb: 3, fontSize: '0.82rem' }}
          >
            Si no lo ves en unos minutos, revisa tu carpeta de spam o correo no deseado.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {!stateEmail && (
              <TextField
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                autoComplete="email"
              />
            )}

            <TextField
              label="Código de verificación"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
              inputProps={{
                maxLength: 6,
                style: { letterSpacing: '0.5em', fontWeight: 700, fontSize: '1.1rem' },
              }}
            />

            <TextField
              label="Nueva contraseña"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError('');
              }}
              error={!!passwordError}
              helperText={passwordError || PASSWORD_RULE_MESSAGE}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              autoComplete="new-password"
              InputProps={{ endAdornment: passwordAdornment }}
            />

            <TextField
              label="Confirmar nueva contraseña"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 3 }}
              autoComplete="new-password"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              sx={{
                bgcolor: colors.primary.main,
                color: 'white',
                fontWeight: 700,
                borderRadius: 2,
                py: 1.3,
                textTransform: 'none',
                fontSize: '0.95rem',
                boxShadow: '0 4px 16px rgba(74,144,226,0.35)',
                '&:hover': { bgcolor: colors.primary.dark },
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: 'white' }} />
              ) : (
                'Cambiar mi contraseña'
              )}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Box
              component={Link}
              to="/forgot-password"
              sx={{
                color: colors.text.secondary,
                fontSize: '0.8rem',
                textDecoration: 'none',
                '&:hover': { color: colors.primary.main },
              }}
            >
              Solicitar un código nuevo
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPassword;
